/**
 * Keycap icon set: original filled silhouette glyphs (not game art), drawn to
 * read like ability/unit icons. Each snippet is the inner markup of an `<svg>` with
 * viewBox 0 0 24 24, filled with currentColor (rendered white on a category badge).
 * Ported from the v2 design handoff's `keylens-icons.js` (the handoff dirs were removed at
 * ship; recover from git history). Swap-ready: point BY_COMMAND at real licensed art URLs and
 * branch in the keycap renderer instead.
 */
const GLYPHS: Record<string, string> = {
  // workers / economy
  worker:
    '<path fill-rule="evenodd" d="M12 2.4l8.4 4.8v9.6L12 21.6 3.6 16.8V7.2zM12 8.2a3.8 3.8 0 1 0 0 7.6 3.8 3.8 0 0 0 0-7.6z"/>',
  // structures
  townhall:
    '<path fill-rule="evenodd" d="M12 3.6l9.2 6.6-1.3 1.8-1-.7v9.1H4.1V11.3l-1 .7L1.8 10.2zM10 13.6v6.8h4v-6.8z"/>',
  supply:
    '<path fill-rule="evenodd" d="M4.6 4.6h14.8v14.8H4.6zm6.2 2.2H6.8v4h4zm6.4 0h-4v4h4zm-6.4 6.4H6.8v4h4zm6.4 0h-4v4h4z"/>',
  production:
    '<path fill-rule="evenodd" d="M3.8 8.4L12 5l8.2 3.4V20H3.8zM9.4 14.2v5.8h5.2v-5.8z"/>',
  gas: '<path d="M12 3.2c3.7 4.7 5.7 7.4 5.7 10.1a5.7 5.7 0 1 1-11.4 0c0-2.7 2-5.4 5.7-10.1z"/>',
  tech: '<path fill-rule="evenodd" d="M7 6.4h10a.9.9 0 0 1 .9.9v9.4a.9.9 0 0 1-.9.9H7a.9.9 0 0 1-.9-.9V7.3a.9.9 0 0 1 .9-.9zm5 3.5a2.1 2.1 0 1 0 0 4.2 2.1 2.1 0 0 0 0-4.2z"/><path d="M9.1 3.4h1.5v2.6H9.1zm4.3 0h1.5v2.6h-1.5zM9.1 18h1.5v2.6H9.1zm4.3 0h1.5v2.6h-1.5zM3.4 9.1H6v1.5H3.4zm0 4.3H6v1.5H3.4zM18 9.1h2.6v1.5H18zm0 4.3h2.6v1.5H18z"/>',
  defense:
    '<path d="M3.8 18.4h16.4v1.8H3.8zM5.4 16.8c0-3.9 3-6.8 6.6-6.8s6.6 2.9 6.6 6.8z"/><path d="M11 5h2v5h-2z"/>',
  reactor:
    '<path fill-rule="evenodd" d="M12 3.4a8.6 8.6 0 1 0 0 17.2 8.6 8.6 0 0 0 0-17.2zm0 4.2a4.4 4.4 0 1 1 0 8.8 4.4 4.4 0 0 1 0-8.8z"/><circle cx="12" cy="12" r="2.1"/>',
  // units
  u_marine: '<path d="M12 2.6l8.2 3.3v5.6c0 5.5-4 9.1-8.2 11.3-4.2-2.2-8.2-5.8-8.2-11.3V5.9z"/>',
  u_marauder:
    '<path fill-rule="evenodd" d="M12 2.6l8.2 3.3v5.6c0 5.5-4 9.1-8.2 11.3-4.2-2.2-8.2-5.8-8.2-11.3V5.9zM7.8 9.1v1.9h8.4V9.1zm0 3.3v1.9h8.4v-1.9z"/>',
  u_reaper:
    '<path d="M3.2 6.6L12 12.2l8.8-5.6 1.4 2.2-10.2 6.5-10.2-6.5zM3.2 11.8L12 17.4l8.8-5.6 1.4 2.2-10.2 6.5-10.2-6.5z"/>',
  u_ghost:
    '<path fill-rule="evenodd" d="M12 4.2a7.8 7.8 0 1 0 0 15.6 7.8 7.8 0 0 0 0-15.6zm0 5.2a2.6 2.6 0 1 1 0 5.2 2.6 2.6 0 0 1 0-5.2z"/>',
  u_zealot:
    '<path d="M12 10.2L4.4 3.6 3.4 6l6.5 6L3.4 18l1 2.4L12 13.8l7.6 6.6 1-2.4-6.5-6 6.5-6-1-2.4z"/>',
  u_stalker:
    '<path d="M12 4.8a3.4 3.4 0 0 1 1.6 6.4l4.4 8.6-1.9 1-3.6-7.1a3.5 3.5 0 0 1-1 0l-3.6 7.1-1.9-1 4.4-8.6A3.4 3.4 0 0 1 12 4.8z"/>',
  u_sentry:
    '<path fill-rule="evenodd" d="M12 3.2l7.8 4.5v8.6L12 20.8 4.2 16.3V7.7zm0 5.4a3.4 3.4 0 1 0 0 6.8 3.4 3.4 0 0 0 0-6.8z"/>',
  u_htempl: '<path fill-rule="evenodd" d="M12 3.4l8.8 16H3.2zm-1 5.6l-1 4.4h4l-1-4.4z"/>',
  u_dtempl: '<path d="M12 2.6l3.8 9.6L12 21.4 8.2 12.2z"/>',
  u_zergling:
    '<path d="M6 3.8c1.5 3.6 3 5.9 4.9 7.6V20.4H8.4v-7.1C6.6 11.3 5.3 8.9 4.4 5.9zM18 3.8c-1.5 3.6-3 5.9-4.9 7.6V20.4h2.5v-7.1c1.8-2 3.1-4.4 4-7.4z"/>',
  u_roach:
    '<path fill-rule="evenodd" d="M3.6 16a8.4 8.4 0 0 1 16.8 0zM8.6 11.4l-1 4.6h1.8l.6-4.4zm5.8 0-.4.2-.6 4.4h1.8l1-4.4z"/>',
  u_hydra:
    '<path d="M10.8 20.6V9.4L6 4.4l1.5-1.5L12 7.4l4.5-4.5L18 4.4l-4.8 5v11.2z"/><path d="M12 11.6l4.8-2.8 1 1.7L12 13.7 6.2 10.5l1-1.7z"/>',
  u_muta:
    '<path d="M11 5.6h2v14h-2z"/><path d="M12 8.8C8.2 5 4 6.8 2.8 9c2.8 1 5 3 9.2 5.4zM12 8.8C15.8 5 20 6.8 21.2 9c-2.8 1-5 3-9.2 5.4z"/>',
  u_overlord:
    '<path fill-rule="evenodd" d="M12 4.6a8.2 5.2 0 1 0 0 10.4 8.2 5.2 0 0 0 0-10.4zM9.1 14.8l1 4.4-1.8.4-1.1-4.2zm5.8 0 .9.6 1.1 4.2-1.8-.4z"/>',
  u_corruptor:
    '<path d="M8.6 6.2a3.4 3.4 0 1 0 0 6.8 3.4 3.4 0 0 0 0-6.8zm6.8 0a3.4 3.4 0 1 0 0 6.8 3.4 3.4 0 0 0 0-6.8zM12 12.2a3.4 3.4 0 1 0 0 6.8 3.4 3.4 0 0 0 0-6.8z"/>',
  u_infestor:
    '<path fill-rule="evenodd" d="M12 6c4.6 0 7.8 3 7.8 6.4 0 2.8-3.2 4.8-7.8 4.8s-7.8-2-7.8-4.8C4.2 9 7.4 6 12 6zm-2.6 4.8a1.4 1.4 0 1 0 0 2.8 1.4 1.4 0 0 0 0-2.8zm5.2 0a1.4 1.4 0 1 0 0 2.8 1.4 1.4 0 0 0 0-2.8z"/>',
  u_queen: '<path d="M3.8 18.8l-1.3-10.4 5 3.9L12 4.6l4.5 7.7 5-3.9-1.3 10.4z"/>',
  u_mship:
    '<path fill-rule="evenodd" d="M12 6.2a5.2 4.2 0 0 1 5.2 4.2H6.8A5.2 4.2 0 0 1 12 6.2zM2.6 11h18.8a9.4 3.2 0 0 1-18.8 0zM10.4 15.2h3.2v3.4h-3.2z"/>',
  // abilities
  a_stim: '<path d="M12 3.2l6 6.4h-3.6v11.2h-4.8V9.6H6z"/>',
  a_cloak:
    '<path fill-rule="evenodd" d="M12 5.4c5.8 0 9.8 6.6 9.8 6.6S17.8 18.6 12 18.6 2.2 12 2.2 12 6.2 5.4 12 5.4zm0 3.3a3.3 3.3 0 1 0 0 6.6 3.3 3.3 0 0 0 0-6.6z"/><path d="M4 3.2l16.8 16.8-1.8 1.8L2.2 5z"/>',
  a_snipe:
    '<path fill-rule="evenodd" d="M12 3.2a8.8 8.8 0 1 0 0 17.6 8.8 8.8 0 0 0 0-17.6zm0 3.6a5.2 5.2 0 1 1 0 10.4 5.2 5.2 0 0 1 0-10.4z"/><path d="M11 1.4h2v5.2h-2zm0 16h2v5.2h-2zM1.4 11h5.2v2H1.4zm16 0h5.2v2h-5.2z"/><circle cx="12" cy="12" r="1.7"/>',
  a_emp:
    '<path d="M12 1.6l2 6 5.6-3.6-2.4 5.7 6.2.2-5 3.7 5 3.7-6.2.2 2.4 5.7L12 19.8l-2 6.2-2-6.2-5.6 3.6 2.4-5.7-6.2-.2 5-3.7-5-3.7 6.2-.2L4.4 4l5.6 3.6z" transform="scale(0.82) translate(2.6 2.6)"/>',
  a_nuke:
    '<path fill-rule="evenodd" d="M12 3.4l8.8 15.8H3.2zm-1 5.8-.3 4.8h2.6l-.3-4.8zm1 6.6a1.2 1.2 0 1 0 0 2.4 1.2 1.2 0 0 0 0-2.4z"/>',
  a_heal: '<path d="M9.4 3.4h5.2v6h6v5.2h-6v6H9.4v-6h-6V9.4h6z"/>',
  a_blink: '<path d="M13.2 2.2L5.4 13.4H11l-1.5 8.8 8.4-12H12z"/>',
  a_chrono:
    '<path fill-rule="evenodd" d="M12 3.4a8.6 8.6 0 1 0 0 17.2 8.6 8.6 0 0 0 0-17.2zm.9 3.6h-1.8v5.4c0 .3.1.5.3.7l3.4 3.4 1.3-1.3-3.2-3.2z"/>',
  a_creep:
    '<path d="M12 9.4a2.6 2.6 0 1 0 0 5.2 2.6 2.6 0 0 0 0-5.2zM5.8 7a1.8 1.8 0 1 0 0 3.6 1.8 1.8 0 0 0 0-3.6zm12.4 0a1.8 1.8 0 1 0 0 3.6 1.8 1.8 0 0 0 0-3.6zM6.6 14.2a1.8 1.8 0 1 0 0 3.6 1.8 1.8 0 0 0 0-3.6zm10.8 0a1.8 1.8 0 1 0 0 3.6 1.8 1.8 0 0 0 0-3.6z"/>',
  a_tunnel: '<path d="M9.6 3.4h4.8v8.6h3.8L12 19.8 5.8 12h3.8zM5 19.8h14v2H5z"/>',
  a_holdfire:
    '<path fill-rule="evenodd" d="M12 3.2a8.8 8.8 0 1 0 0 17.6 8.8 8.8 0 0 0 0-17.6zm0 2.4c1.5 0 2.9.4 4.1 1.2L6.8 16.1A6.6 6.6 0 0 1 12 5.6zm5.2 2.7A6.6 6.6 0 0 1 12 18.4c-1.5 0-2.9-.4-4.1-1.2z"/>',
  // commands
  c_move: '<path d="M7.6 5h11.4v11.4h-3.4v-5.6l-7.5 7.6L5.6 16l7.5-7.6H7.6z"/>',
  c_attack:
    '<path fill-rule="evenodd" d="M12 3.2a8.8 8.8 0 1 0 0 17.6 8.8 8.8 0 0 0 0-17.6zm0 4.4a4.4 4.4 0 1 1 0 8.8 4.4 4.4 0 0 1 0-8.8z"/><circle cx="12" cy="12" r="2"/>',
  c_stop: '<path d="M8.1 3.4h7.8l5.3 5.3v7.8l-5.3 5.3H8.1l-5.3-5.3V8.7z"/>',
  c_hold:
    '<path fill-rule="evenodd" d="M6.6 10.4V8a5.4 5.4 0 0 1 10.8 0v2.4h.6a1.5 1.5 0 0 1 1.5 1.5v7.2a1.5 1.5 0 0 1-1.5 1.5H6.2a1.5 1.5 0 0 1-1.5-1.5v-7.2a1.5 1.5 0 0 1 1.5-1.5zm2.4 0h6V8a3 3 0 0 0-6 0z"/>',
  c_patrol:
    '<path d="M12 4.4a7.6 7.6 0 0 1 7.2 5.2l1.9-.7-1.4 5.6-4.8-3.2 2-.8A4.9 4.9 0 0 0 7.1 12H4.5A7.6 7.6 0 0 1 12 4.4zm0 15.2a7.6 7.6 0 0 1-7.2-5.2l-1.9.7 1.4-5.6 4.8 3.2-2 .8A4.9 4.9 0 0 0 16.9 12h2.6A7.6 7.6 0 0 1 12 19.6z"/>',
  c_repair:
    '<path d="M16.8 3.2a5.2 5.2 0 0 0-5.7 6.8l-6.7 6.7a1.7 1.7 0 0 0 0 2.4l1.3 1.3a1.7 1.7 0 0 0 2.4 0l6.7-6.7a5.2 5.2 0 0 0 6.8-5.7l-3.1 3.1-2.7-.7-.7-2.7z"/>',
  c_lift: '<path d="M12 4l7.4 7-2.1 2-5.3-5-5.3 5-2.1-2zm0 6.8l7.4 7-2.1 2-5.3-5-5.3 5-2.1-2z"/>',
  // camera / macro
  cam_idle:
    '<path d="M4.6 7.4h7.8v2.2L7.9 14.4h4.8v2.2H4.2v-2.2l4.5-4.8H4.6zM13.8 4.6h5.8v1.9l-3.3 3.9h3.4v1.9h-6.2v-1.9l3.3-3.9h-3z"/>',
  cam_town: '<path d="M12 4l8.6 6.6-1.3 1.7-1.1-.8v8.3H5.8v-8.3l-1.1.8L3.4 10.6z"/>',
  cam_army:
    '<path d="M8 5.4a3 3 0 1 0 0 6 3 3 0 0 0 0-6zm8 0a3 3 0 1 0 0 6 3 3 0 0 0 0-6zM8 12.4c-3 0-5.4 2-5.4 4.9v1.5h7.6v-1.5c0-1.1.3-2.1.9-3a6.4 6.4 0 0 0-3.1-1.9zm8 0c-.9 0-1.8.2-2.5.5 1 1 1.5 2.4 1.5 4v1.4H21.4v-1.5c0-2.9-2.4-4.9-5.4-4.9z"/>',
};

/**
 * Command id (the SC2_COMMANDS / Binding.cmd id) -> glyph id. Reuses one glyph where
 * units/buildings are functionally the same across races.
 */
const BY_COMMAND: Record<string, string> = {
  // global commands
  move: "c_move",
  attack: "c_attack",
  stop: "c_stop",
  hold: "c_hold",
  patrol: "c_patrol",
  repair: "c_repair",
  lift: "c_lift",
  // camera / macro
  idle: "cam_idle",
  town: "cam_town",
  army: "cam_army",
  // terran abilities
  stim: "a_stim",
  cloak: "a_cloak",
  snipe: "a_snipe",
  emp: "a_emp",
  holdfire: "a_holdfire",
  nuke: "a_nuke",
  heal: "a_heal",
  novasnipe: "a_snipe",
  novanuke: "a_nuke",
  // terran build
  bCmdCtr: "townhall",
  bDepot: "supply",
  bRax: "production",
  bRef: "gas",
  bEbay: "tech",
  bBunker: "defense",
  // terran train
  tMarine: "u_marine",
  tMaraud: "u_marauder",
  tReaper: "u_reaper",
  tGhost: "u_ghost",
  aReactor: "reactor",
  tScv: "worker",
  mOrbital: "townhall",
  // zerg morph
  zDrone: "worker",
  zZergling: "u_zergling",
  zRoach: "u_roach",
  zHydra: "u_hydra",
  zMuta: "u_muta",
  zOver: "u_overlord",
  zCorrupt: "u_corruptor",
  zInfest: "u_infestor",
  // zerg queen / build
  qCreep: "a_creep",
  qTunnel: "a_tunnel",
  zbHatch: "townhall",
  zbPool: "production",
  zbExtract: "gas",
  zbEvo: "tech",
  zbRoachW: "tech",
  zbHydra: "tech",
  zbBane: "tech",
  // protoss build
  pNexus: "townhall",
  pPylon: "supply",
  pGate: "production",
  pAssim: "gas",
  pCyber: "tech",
  pForge: "tech",
  pCannon: "defense",
  // protoss train / ability
  pZealot: "u_zealot",
  pStalker: "u_stalker",
  pSentry: "u_sentry",
  pHT: "u_htempl",
  pDT: "u_dtempl",
  pBlink: "a_blink",
  pProbe: "worker",
  pMother: "u_mship",
  pChrono: "a_chrono",
};

/** The icon glyph (svg inner markup) for a command id, or null when none is mapped. */
export function iconGlyph(cmd: string | null | undefined): string | null {
  if (!cmd) return null;
  const gid = BY_COMMAND[cmd];
  return gid ? (GLYPHS[gid] ?? null) : null;
}
